import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { POSITIONS, FOOT, AFRICAN_COUNTRIES } from "../lib/constants";
import { ShieldCheck, Send, Search, MapPin, Plane, Mail, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import TrendingByNation from "../components/TrendingByNation";
import { useTranslation } from "react-i18next";

const ANY = "__any__";
const VERIFIED_LS_KEY = "afri_scout_verified_only";

export default function ScoutDashboard() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(() => ({
    country: "", position: "", dominant_foot: "", min_age: "", max_age: "",
    min_height: "", min_speed: "", min_stamina: "",
    verified_only: localStorage.getItem(VERIFIED_LS_KEY) === "1",
  }));
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [invite, setInvite] = useState({ club_name: "", location: "", date: "", message: "" });
  const [myInvites, setMyInvites] = useState([]);
  const [relocations, setRelocations] = useState([]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
      const { data } = await api.get("/players", { params });
      setPlayers(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [filters]);

  const loadInvites = useCallback(async () => {
    try {
      const [i, r] = await Promise.all([
        api.get("/scout/invites").then((r) => r.data),
        api.get("/relocation/scout").then((r) => r.data),
      ]);
      setMyInvites(i || []);
      setRelocations(r || []);
    } catch (err) { console.debug("loadInvites failed", err?.response?.status); }
  }, []);

  useEffect(() => { search(); loadInvites(); }, [search, loadInvites]);


  const sendInvite = async () => {
    try {
      await api.post("/scout/invite", { ...invite, player_id: selected.user_id });
      toast.success("Invitation sent");
      setSelected(null);
      setInvite({ club_name: "", location: "", date: "", message: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const toggleStep = async (rid, step, done) => {
    try {
      await api.patch(`/relocation/${rid}/step`, { step_key: step.key, done });
      setRelocations((prev) => prev.map((r) => {
        if (r.id !== rid) return r;
        const newSteps = r.steps.map((s) => (s.key === step.key ? { ...s, done } : s));
        const allDone = newSteps.every((s) => s.done);
        return { ...r, steps: newSteps, status: allDone ? "completed" : "in_progress" };
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const paySuccessFee = async (rid) => {
    try {
      const { data } = await api.post(`/relocation/${rid}/checkout-success-fee?origin_url=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b] focus-visible:ring-1 focus-visible:ring-[#e65f2b]";

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("scoutDashboard.overline")}</div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">{t("scoutDashboard.title")}</h1>

        <Tabs defaultValue="discover" className="mt-6">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start p-0 h-auto">
            {[["discover", t("scoutDashboard.discoverTab")], ["invites", t("scoutDashboard.invitesTab")], ["relocation", t("scoutDashboard.relocationTab")]].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`scout-tab-${v}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#e65f2b] data-[state=active]:bg-transparent data-[state=active]:text-[#e65f2b] uppercase tracking-widest text-xs font-bold px-4 py-3"
              >{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="discover" className="mt-6">

        <TrendingByNation compact />

        <div className="mt-6 border border-white/10 bg-[#141414] p-5">
          {/* Prominent verified toggle */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 mb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#eab308]/10 border border-[#eab308]/40 flex items-center justify-center">
                <ShieldCheck size={16} className="text-[#eab308]" />
              </div>
              <div>
                <div className="font-display uppercase text-sm font-bold tracking-widest">{t("players.verifiedOnly")}</div>
                <div className="text-[10px] text-white/50 mt-0.5">{t("players.verifiedOnlyHint")}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs uppercase tracking-widest ${filters.verified_only ? "text-[#eab308]" : "text-white/40"}`}>
                {filters.verified_only ? t("admin.verifyOn") : t("admin.verifyOff")}
              </span>
              <Switch
                data-testid="filter-verified"
                checked={filters.verified_only}
                onCheckedChange={(v) => {
                  setFilters({ ...filters, verified_only: v });
                  localStorage.setItem(VERIFIED_LS_KEY, v ? "1" : "0");
                }}
                className="data-[state=checked]:bg-[#eab308]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.country")}</Label>
            <Select value={filters.country || ANY} onValueChange={(v) => setFilters({ ...filters, country: v === ANY ? "" : v })}>
              <SelectTrigger data-testid="filter-country" className={`${inputCls} mt-2`}><SelectValue placeholder={t("scoutDashboard.filter.any")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("scoutDashboard.filter.any")}</SelectItem>
                {AFRICAN_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.position")}</Label>
            <Select value={filters.position || ANY} onValueChange={(v) => setFilters({ ...filters, position: v === ANY ? "" : v })}>
              <SelectTrigger data-testid="filter-position" className={`${inputCls} mt-2`}><SelectValue placeholder={t("scoutDashboard.filter.any")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("scoutDashboard.filter.any")}</SelectItem>
                {POSITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.foot")}</Label>
            <Select value={filters.dominant_foot || ANY} onValueChange={(v) => setFilters({ ...filters, dominant_foot: v === ANY ? "" : v })}>
              <SelectTrigger data-testid="filter-foot" className={`${inputCls} mt-2`}><SelectValue placeholder={t("scoutDashboard.filter.any")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("scoutDashboard.filter.any")}</SelectItem>
                {FOOT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.minAge")}</Label>
            <Input type="number" data-testid="filter-min-age" value={filters.min_age} onChange={(e) => setFilters({ ...filters, min_age: e.target.value })} className={`${inputCls} mt-2`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.maxAge")}</Label>
            <Input type="number" data-testid="filter-max-age" value={filters.max_age} onChange={(e) => setFilters({ ...filters, max_age: e.target.value })} className={`${inputCls} mt-2`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.minHeight")}</Label>
            <Input type="number" data-testid="filter-min-height" value={filters.min_height} onChange={(e) => setFilters({ ...filters, min_height: e.target.value })} className={`${inputCls} mt-2`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.minSpeed")}</Label>
            <Input type="number" step="0.1" data-testid="filter-min-speed" value={filters.min_speed || ""} onChange={(e) => setFilters({ ...filters, min_speed: e.target.value })} className={`${inputCls} mt-2`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-white/70">{t("scoutDashboard.filter.minStamina")}</Label>
            <Input type="number" data-testid="filter-min-stamina" value={filters.min_stamina || ""} onChange={(e) => setFilters({ ...filters, min_stamina: e.target.value })} className={`${inputCls} mt-2`} />
          </div>
          <div className="md:col-span-12 flex justify-end">
            <Button onClick={search} disabled={loading} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold rounded-sm uppercase tracking-widest h-11 px-8" data-testid="search-players-btn">
              <Search size={14} className="mr-2" />{loading ? `${t("common.loading")}` : t("common.search")}
            </Button>
          </div>
          </div>
        </div>

        <div className="mt-6 text-xs uppercase tracking-widest text-white/60">{t("common.playersCount", { count: players.length })}</div>
        <div className="mt-4 grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {players.map((p) => (
            <div key={p.id || p.user_id} className="border border-white/10 bg-[#141414] hover:border-[#e65f2b] transition-colors" data-testid={`player-card-${p.user_id}`}>
              <div className="aspect-[4/5] bg-black relative overflow-hidden">
                {p.photo_path
                  ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center font-display text-6xl text-white/20">{(p.name || "?")[0]}</div>}
                {p.verified && (
                  <div className="absolute top-3 right-3 bg-[#eab308] text-black px-2 py-1 text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                    <ShieldCheck size={12} />Verified
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="font-display text-lg font-bold uppercase truncate flex items-center gap-1.5">
                  <span className="truncate">{p.name}</span>
                  {p.verified && <ShieldCheck size={14} className="text-[#eab308] shrink-0" data-testid="scout-card-verified-badge" />}
                </div>
                <div className="text-xs text-white/60 mt-1 flex items-center gap-1"><MapPin size={12} />{p.country || "—"} · {p.nationality || "—"}</div>
                <div className="text-xs text-white/70 uppercase tracking-widest mt-2">{p.position || "—"} · {p.age ? `${p.age}y` : "—"}</div>
                <div className="mt-3 flex gap-2">
                  <Link to={`/players/${p.user_id}`} className="flex-1">
                    <Button variant="outline" className="w-full border-white/20 hover:bg-white/10 rounded-sm text-xs uppercase tracking-widest h-9" data-testid={`view-player-${p.user_id}`}>View</Button>
                  </Link>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button onClick={() => setSelected(p)} className="flex-1 bg-[#e65f2b] hover:bg-[#c54a1c] text-black rounded-sm text-xs uppercase tracking-widest h-9 font-bold" data-testid={`invite-player-${p.user_id}`}>
                        <Send size={12} className="mr-1" />Invite
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle className="font-display uppercase text-2xl">Trial invitation</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-white/70">Club name</Label>
                          <Input value={invite.club_name} onChange={(e) => setInvite({ ...invite, club_name: e.target.value })} className={`${inputCls} mt-2`} data-testid="invite-club-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-white/70">Location</Label>
                          <Input value={invite.location} onChange={(e) => setInvite({ ...invite, location: e.target.value })} className={`${inputCls} mt-2`} data-testid="invite-location-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-white/70">Date</Label>
                          <Input type="date" value={invite.date} onChange={(e) => setInvite({ ...invite, date: e.target.value })} className={`${inputCls} mt-2`} data-testid="invite-date-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-white/70">Message</Label>
                          <Textarea value={invite.message} onChange={(e) => setInvite({ ...invite, message: e.target.value })} className={`${inputCls} min-h-24 mt-2`} data-testid="invite-message-input" />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={sendInvite} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm" data-testid="invite-submit-btn">Send invitation</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
          {players.length === 0 && !loading && <div className="text-white/50 md:col-span-4">No players match the current filters.</div>}
        </div>
          </TabsContent>

          <TabsContent value="invites" className="mt-6">
            <div className="space-y-3">
              {myInvites.length === 0 && <div className="text-white/50">You haven&apos;t sent any invitations yet.</div>}
              {myInvites.map((i) => (
                <div key={i.id} className="border border-white/10 bg-[#141414] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`scout-invite-${i.id}`}>
                  <div className="flex-1 min-w-64">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-[#eab308]" />
                      <div className="font-display text-lg uppercase font-bold">{i.club_name}</div>
                    </div>
                    <div className="text-sm text-white/60 mt-1">{i.location} · {i.date}</div>
                    <div className="text-sm mt-2 text-white/80">{i.message}</div>
                  </div>
                  <div
                    className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${
                      i.status === "accepted" ? "text-[#2A9D8F] border border-[#2A9D8F]/40" :
                      i.status === "declined" ? "text-red-400 border border-red-400/40" :
                      "text-white/60 border border-white/20"
                    }`}
                    data-testid={`scout-invite-status-${i.id}`}
                  >
                    {i.status}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="relocation" className="mt-6">
            <div className="space-y-4">
              {relocations.length === 0 && (
                <div className="text-white/50">No active relocations. They open automatically when a player accepts your invite.</div>
              )}
              {relocations.map((r) => {
                const total = r.steps.length;
                const done = r.steps.filter((s) => s.done).length;
                const pct = Math.round((done / total) * 100);
                return (
                  <div key={r.id} className="border border-white/10 bg-[#141414] p-6" data-testid={`scout-relocation-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 text-[#eab308]">
                          <Plane size={16} />
                          <span className="text-xs uppercase tracking-widest">{r.player_name} → {r.club_name}</span>
                        </div>
                        <div className="font-display text-2xl uppercase font-bold mt-2">{r.destination}</div>
                        <div className="text-sm text-white/60">Target: {r.target_date} · Status: {r.status}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl font-bold text-[#e65f2b]">{pct}%</div>
                        <div className="text-xs uppercase tracking-widest text-white/50">{done} / {total} steps</div>
                      </div>
                    </div>
                    <Progress value={pct} className="mt-4" />
                    {pct === 100 && !r.success_fee_paid && (
                      <div className="mt-4 border border-[#eab308]/40 bg-[#eab308]/5 p-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="font-display uppercase font-bold text-[#eab308]">Relocation complete</div>
                          <div className="text-xs text-white/60">Time to close the success fee ($500 one-time).</div>
                        </div>
                        <Button
                          onClick={() => paySuccessFee(r.id)}
                          data-testid={`pay-success-fee-${r.id}`}
                          className="bg-[#eab308] hover:bg-[#c58d00] text-black font-bold uppercase tracking-widest rounded-sm h-10"
                        >
                          <DollarSign size={14} className="mr-1" />Pay success fee
                        </Button>
                      </div>
                    )}
                    <div className="mt-6 grid md:grid-cols-2 gap-2">
                      {r.steps.map((s) => (
                        <label
                          key={s.key}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            s.done ? "border-[#2A9D8F]/40 bg-[#2A9D8F]/5" : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`scout-reloc-step-${r.id}-${s.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={s.done}
                            onChange={(e) => toggleStep(r.id, s, e.target.checked)}
                            className="w-4 h-4 accent-[#2A9D8F]"
                          />
                          <div className={`text-sm uppercase tracking-widest font-bold ${s.done ? "text-[#2A9D8F]" : "text-white"}`}>
                            {s.label}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
